import { createApiErrorTransformer } from '@/lib/api/baseQuery';
import { createBaseQuery } from '@/lib/api/baseConfig';
import { createApi } from '@reduxjs/toolkit/query/react';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface NewsItem {
  _id: string;
  news_source: string;
  news_url: string;
  categories: string[];
  image_url: string;
  main_category: string;
  news_caption: string;
  news_date: string;
  news_summary: string;
  news_time: string;
  scraped_at: string;
  top_news: boolean;
  top_score: number;
  unique_id: string;
}

export interface NewsResponse {
  data: NewsItem[];
  total: number;
  page: number;
  limit: number;
}

export interface GetNewsParams {
  page?: number;
  limit?: number;
  main_category?: string;
  news_source?: string;
  search_caption?: string;
  top_news?: boolean;
}

// ── Response normalizer ────────────────────────────────────────────────────────
// Actual API shape:
// { statusCode: 200, data: { items: [...], total: N, page: N, limit: N }, message: "...", status: true }

function normalizeNewsResponse(raw: any): NewsResponse {
  // Unwrap outer envelope: raw.data holds { items, total, page, limit }
  const inner = raw?.data ?? raw;

  const items: NewsItem[] = Array.isArray(inner?.items)
    ? inner.items
    : Array.isArray(inner?.data)
      ? inner.data
      : Array.isArray(raw)
        ? raw
        : [];

  return {
    data: items,
    total: inner?.total ?? items.length,
    page: inner?.page ?? 1,
    limit: inner?.limit ?? items.length,
  };
}

// ── API ────────────────────────────────────────────────────────────────────────

export const newsApi = createApi({
  reducerPath: 'newsApi',
  baseQuery: createBaseQuery('news', {
    baseUrl: import.meta.env.VITE_CONNECT_URL,
  }),
  tagTypes: ['News'],
  endpoints: (builder) => ({
    getNews: builder.query<NewsResponse, GetNewsParams | void>({
      query: (arg) => ({
        url: '/v2/news',
        params: {
          source: 'ap',
          ...(arg?.top_news ? { top_news: arg.top_news } : {}),
          ...(arg?.page ? { page: arg.page } : {}),
          ...(arg?.limit ? { limit: arg.limit } : {}),
          ...(arg?.main_category ? { main_category: arg.main_category } : {}),
          ...(arg?.news_source ? { news_source: arg.news_source } : {}),
          ...(arg?.search_caption ? { search_caption: arg.search_caption } : {}),
        },
      }),
      transformResponse: normalizeNewsResponse,
      transformErrorResponse: createApiErrorTransformer('News', false),
    }),
  }),
});

export const { useGetNewsQuery } = newsApi;
